/**
 * Personal Access Token (PAT) — long-lived opaque tokens issued to MCP/OAuth clients
 * (e.g. Claude Code) and, later, user-scoped scripting/CI flows. Each PAT acts as the
 * user it was minted for, scoped to one org and one (resource, surface) pair per
 * RFC 8707 enforcement.
 *
 * Browser-safe constants only. The verification + minting logic lives in
 * server/src/utils/auth/personalAccessToken.ts.
 */

/**
 * Personal Access Token prefix. All PATs start with this.
 * Format: cup_<32-char base62>. The "cup" stands for "Curium User PAT" — distinct
 * from `cu_` (Service API Token / machine-to-machine).
 */
export const PERSONAL_ACCESS_TOKEN_PREFIX = 'cup_' as const;

/**
 * Length of the random portion (after the prefix).
 */
export const PERSONAL_ACCESS_TOKEN_RANDOM_LENGTH = 32 as const;

/**
 * Total plaintext PAT length: 'cup_' (4) + 32 random chars = 36.
 */
export const PERSONAL_ACCESS_TOKEN_TOTAL_LENGTH =
    PERSONAL_ACCESS_TOKEN_PREFIX.length + PERSONAL_ACCESS_TOKEN_RANDOM_LENGTH;

/**
 * Length stored in `tokenPrefix` for UI/operator display. Includes the literal
 * "cup_" prefix plus 8 characters of the random suffix.
 */
export const PERSONAL_ACCESS_TOKEN_DISPLAY_PREFIX_LENGTH = 12 as const;

/**
 * Allowed expiry values (in days) for new PATs. Enforced server-side at mint time.
 *
 * OAuth-issued PATs cap at 90 days per the security model — AI tokens carry higher
 * risk than human-session tokens and the silent Auth0 re-auth flow makes quarterly
 * re-authorization a click-once experience.
 *
 * UI-issued PATs (future feature, not yet implemented) will permit a longer cap
 * up to 365 days for headless / CI use cases.
 */
export const PERSONAL_ACCESS_TOKEN_OAUTH_EXPIRY_DAYS = [30, 60, 90] as const;
export const PERSONAL_ACCESS_TOKEN_UI_EXPIRY_DAYS = [30, 60, 90, 180, 365] as const;

export const PERSONAL_ACCESS_TOKEN_OAUTH_MAX_EXPIRY_DAYS = 90 as const;
export const PERSONAL_ACCESS_TOKEN_UI_MAX_EXPIRY_DAYS = 365 as const;

/**
 * `mcpOps` (`/mcp-ops`) tokens are short incident-scoped prod-read credentials, not
 * standing operator tokens — capped hard at 7 days. Enforced in the shared mint
 * primitive (`mintOpsAccessToken`), not just the `admin.opsRead.mintToken` route
 * schema, so no future caller of the primitive can mint a long-lived prod-read token.
 */
export const PERSONAL_ACCESS_TOKEN_MCP_OPS_MAX_EXPIRY_DAYS = 7 as const;

/**
 * `mcpAdmin` (`/mcp-admin`) tokens minted or OAuth-issued **in production** are capped at 30
 * days — a standing super-admin bearer credential against production must be re-established
 * regularly. Enforced in `mintOpsAccessToken` and the OAuth token exchange, not just the UI.
 * Development and staging keep the regular UI/OAuth caps.
 */
export const PERSONAL_ACCESS_TOKEN_MCP_ADMIN_PROD_MAX_EXPIRY_DAYS = 30 as const;
export const PERSONAL_ACCESS_TOKEN_MCP_ADMIN_PROD_EXPIRY_DAYS = [7, 14, 30] as const;

/**
 * Operator tokens (`mcpAdmin` / `mcpOps`) may carry an optional IP allowlist (single addresses
 * or CIDR ranges). Empty = no restriction. Bounded so a token row stays small and the per-request
 * check stays cheap.
 */
export const PERSONAL_ACCESS_TOKEN_MAX_IP_ALLOWLIST_ENTRIES = 10 as const;

/**
 * Narrowest CIDR prefix an operator-token allowlist entry may use, per address family. Without
 * a floor, `0.0.0.0/0` or `::/0` would satisfy the production "IP allowlist required" rule
 * while restricting nothing. /16 (IPv4) and /32 (IPv6) still admit an ISP or corporate block.
 */
export const PERSONAL_ACCESS_TOKEN_MIN_IP_ALLOWLIST_PREFIX_LENGTH = { 4: 16, 6: 32 } as const;

/** Bounds for the free-text mint reason (incident/ticket attribution) on operator tokens. */
export const PERSONAL_ACCESS_TOKEN_REASON_MIN_LENGTH = 5 as const;
export const PERSONAL_ACCESS_TOKEN_REASON_MAX_LENGTH = 500 as const;

/**
 * The single v1 scope. Token acts as the user with their full permissions.
 * Future versions may introduce narrower scopes (e.g. read-only).
 */
export const PERSONAL_ACCESS_TOKEN_SCOPE_FULL = 'curium:full' as const;
export type PersonalAccessTokenScope = typeof PERSONAL_ACCESS_TOKEN_SCOPE_FULL;

/**
 * Allowed surfaces where a PAT may be presented. The auth path rejects any
 * presentation outside the token's `allowedSurfaces` set.
 *
 * - `mcp` / `trpc` — the tenant MCP and internal tRPC surfaces.
 * - `mcpAdmin` — the system-console admin MCP (`/mcp-admin`; dev/staging, and
 *   production when `ADMIN_MCP_ENABLED` is deployed). An admin PAT is un-replayable
 *   on `/mcp` or `/trpc/*` (and vice-versa) via the surface + resource binding
 *   checked in `verifyPersonalAccessToken`.
 * - `mcpOps` — the read-only production ops MCP (`/mcp-ops`), gated per
 *   environment by `OPS_READ_MCP_ENABLED`. Deliberately a separate surface from
 *   `mcpAdmin` so a prod-read token can never call the demo-data write tools
 *   and vice-versa.
 */
export const PERSONAL_ACCESS_TOKEN_SURFACES = ['mcp', 'trpc', 'mcpAdmin', 'mcpOps'] as const;
export type PersonalAccessTokenSurface = (typeof PERSONAL_ACCESS_TOKEN_SURFACES)[number];

/**
 * Quick format check (cheap, runs before any DB lookup).
 */
export function isPersonalAccessTokenFormat(token: string): boolean {
    return (
        typeof token === 'string' &&
        token.startsWith(PERSONAL_ACCESS_TOKEN_PREFIX) &&
        token.length === PERSONAL_ACCESS_TOKEN_TOTAL_LENGTH
    );
}
